import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const page: ContentPageData = {
  path: '/dlya/nerukhomosti',
  parent: { name: 'Галузі', path: '/dlya' },
  crumb: 'Агенції нерухомості',
  title: 'AI-агент для агенції нерухомості | Anytel',
  description:
    'AI-агент відповідає на дзвінки за оголошеннями 24/7: розповідає про об’єкт, уточнює запит клієнта, записує на перегляд і передає ліда ріелтору.',
  eyebrow: 'Для агенцій нерухомості',
  h1: 'AI-агент для агенції нерухомості',
  lead:
    'Дзвінки за оголошеннями приходять будь-коли, а ріелтор саме на показі. AI-агент відповідає замість нього: розповідає про об’єкт, уточнює, що шукає клієнт, і записує на перегляд.',
  sections: [
    {
      h2: 'Де губляться клієнти агенції',
      blocks: [
        'Ріелтор більшу частину дня в дорозі й на показах. Дзвінок за оголошенням, який він не взяв, рідко повторюється: людина телефонує за наступним оголошенням у списку. Навіть якщо ріелтор передзвонить за годину, клієнт може вже домовитись про перегляд в іншій агенції.',
        {
          list: [
            'Показ у розпалі, телефон у кишені, дзвінок пропущено.',
            'Багато дзвінків з тими самими питаннями: чи ще актуально, який поверх, чи можна з тваринами.',
            'Ліди з різних оголошень записують у різних місцях, і частина губиться.',
          ],
        },
      ],
    },
    {
      h2: 'Що робить AI-агент',
      blocks: [
        { h3: 'Відповідає про об’єкти' },
        'Ви додаєте в базу знань актуальні об’єкти: район, площу, поверх, ціну, умови. Агент відповідає на питання про конкретне оголошення і не вигадує характеристик, яких немає в описі. Якщо об’єкт уже не актуальний, він може запропонувати схожі з тих, що є в базі.',
        { h3: 'Уточнює запит' },
        'Купівля чи оренда, бюджет, район, кількість кімнат, терміни. Агент ставить кваліфікаційні питання, які ви задали, і ріелтор отримує зрозумілого клієнта, а не просто номер телефону.',
        { h3: 'Записує на перегляд' },
        'У кожного ріелтора свій календар з графіком показів. Агент пропонує вільний час і записує на перегляд, а ріелтор отримує сповіщення з деталями.',
        { h3: 'Передає гарячих клієнтів одразу' },
        'Якщо клієнт готовий вносити завдаток чи хоче говорити з ріелтором прямо зараз, агент переводить дзвінок на вільного співробітника або в чергу операторів.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад розмови',
    context: 'Агенція нерухомості, неділя, 11:20',
    dialog: [
      { who: 'caller', text: 'Добрий день, я за оголошенням про двокімнатну на Лук’янівці. Вона ще актуальна?' },
      { who: 'ai', text: 'Добрий день! Так, квартира ще здається: 64 квадратні метри, шостий поверх, 22 000 гривень на місяць. Ви шукаєте для себе чи для родини?' },
      { who: 'caller', text: 'Для себе з дружиною. І в нас кіт.' },
      { who: 'ai', text: 'Власник не проти тварин. Переглянути квартиру можна завтра о 18:30 або в середу о 12:00 з ріелторкою Іриною. Як вам зручніше?' },
      { who: 'caller', text: 'Завтра о пів на сьому. Мене звати Максим.' },
      { who: 'ai', text: 'Записала, Максиме: завтра, 18:30, перегляд на Лук’янівці. Ірина зателефонує вам перед показом.' },
    ],
    result: {
      title: 'Перегляд у календарі',
      rows: [
        ['Клієнт', 'Максим'],
        ['Об’єкт', '2 кімнати, Лук’янівка, оренда'],
        ['Коли', 'Завтра, 18:30'],
        ['Ріелтор', 'Ірина'],
      ],
      destination: 'Ірина отримала сповіщення в Telegram з деталями запиту',
    },
  },
  after: [
    {
      h2: 'Усі ліди в одному місці',
      blocks: [
        'Дзвінки з різних оголошень потрапляють в один журнал. Кожен лід має контакт, запит і запис розмови, тож керівник бачить, скільки звернень було і що з ними сталось. Через [вебхуки й API](/funktsii/integratsiya-z-crm) ліди передаються у вашу CRM.',
        'Різним рекламним каналам можна дати окремі номери чи SIP-адреси і бачити в журналі, звідки прийшов дзвінок.',
      ],
    },
    {
      h2: 'Як підключити агенцію',
      blocks: [
        {
          steps: [
            { title: 'Об’єкти в базі знань', text: 'Додайте актуальні оголошення з основними характеристиками. Оновлювати їх можна в кабінеті будь-коли.' },
            { title: 'Календарі ріелторів', text: 'Створіть для кожного ріелтора календар з графіком показів.' },
            { title: 'Номер', text: 'Переадресуйте дзвінки з номера, вказаного в оголошеннях, або підключіть новий через свого SIP-оператора.' },
          ],
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Як агент дізнається про нові об’єкти?',
      a: 'Ви додаєте їх у базу знань у кабінеті. Якщо об’єкти ведуться у вашій системі, агент може звертатися до неї через API під час розмови й брати актуальні дані.',
    },
    {
      q: 'Чи може агент домовлятися про ціну?',
      a: 'Ні, і не повинен. Він назве ціну з оголошення, а питання знижки чи умов передасть ріелтору.',
    },
    {
      q: 'Що, якщо людина дзвонить, щоб продати свою квартиру?',
      a: 'Агент приймає таку заявку окремо: що за об’єкт, район, бажана ціна, контакт. І передає її відповідальному співробітнику.',
    },
    {
      q: 'Чи знатимуть клієнти, що говорять з AI?',
      a: 'Так. На початку розмови звучить коротке попередження про запис і віртуального асистента. Це чесно і відповідає вимогам до запису розмов.',
    },
  ],
  related: ['/funktsii/ai-agent-dlya-dzvinkiv', '/funktsii/integratsiya-z-crm', '/funktsii/zapys-i-transkryptsiya-dzvinkiv', '/blog/ai-agent-chy-administrator'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
